import Box from '@totallymoney/ui/components/Box'
import Divider from '@totallymoney/ui/components/Divider'
import Flex from '@totallymoney/ui/components/Flex'
import Stack from '@totallymoney/ui/components/Stack'

import {
    GetUnscoredCategoriesFilter,
    UnscoredLoanProduct,
} from '@appTypes/LoansApi.interface'

import RepresentativeExample from '@components/RepresentativeExample'

import { canShowSameDayDepositBadge } from '@utils/canShowSameDayDepositBadge'

import UnscoredTableItemBadgesSection from './components/UnscoredTableItemBadgesSection'
import UnscoredTableItemCard from './components/UnscoredTableItemCard'
import UnscoredTableItemOffersSection from './components/UnscoredTableItemOffersSection'
import UnscoredTableItemProductInfoSection from './components/UnscoredTableItemProductInfoSection'
import UnscoredTableItemTitleSection from './components/UnscoredTableItemTitleSection'

interface UnscoredTableItemProps {
    filter: GetUnscoredCategoriesFilter
    item: UnscoredLoanProduct
    onShowLoanTypeModal: () => void
    onShowRepresentativeExampleModal: () => void
    renderButtons: (item: UnscoredLoanProduct) => React.ReactNode
}

const UnscoredTableItem = ({
    item,
    filter: { loanAmount, loanTerm },
    onShowRepresentativeExampleModal,
    onShowLoanTypeModal,
    renderButtons,
}: UnscoredTableItemProps) => {
    const { hasOffer, offerDescription, offerTitle, propositionId } = item

    const hasSameDayDepositBadge = canShowSameDayDepositBadge(item.loanId)

    const canShowBadgesSection = hasSameDayDepositBadge
    const canShowOffersSection =
        (hasOffer && (offerTitle || offerDescription)) || hasSameDayDepositBadge

    return (
        <UnscoredTableItemCard id={propositionId}>
            <Stack
                space="space20"
                display={['flex', 'flex', 'grid']}
                mb="space30"
            >
                <Box gridColumn="1/13" gridRow={1}>
                    {canShowBadgesSection ? (
                        <UnscoredTableItemBadgesSection
                            hasSameDayDepositBadge={hasSameDayDepositBadge}
                        />
                    ) : null}

                    <UnscoredTableItemTitleSection
                        item={item}
                        onShowLoanTypeModal={onShowLoanTypeModal}
                    />
                </Box>

                <Box gridColumn="1/12" gridRow={2}>
                    <UnscoredTableItemProductInfoSection item={item} />
                </Box>

                {canShowOffersSection ? (
                    <Box gridColumn="1/13" gridRow={3}>
                        <Box
                            mb="space20"
                            width="100%"
                            display={['none', 'block']}
                        >
                            <Divider
                                borderStyle="dashed"
                                color="grey30"
                                spacing={0}
                            />
                        </Box>

                        <UnscoredTableItemOffersSection
                            hasSameDayDepositBadge={hasSameDayDepositBadge}
                            item={item}
                        />
                    </Box>
                ) : null}

                <Flex
                    gridColumn="12/13"
                    gridRow={2}
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <Box maxWidth="280px" width={['100%', '100%', '212px']}>
                        {renderButtons(item)}
                    </Box>
                </Flex>
            </Stack>

            <Box
                backgroundColor="grey10"
                m={['-space20', '-space30']}
                mt={[0, 'space30']}
                p="space30"
                pt="space15"
            >
                <RepresentativeExample
                    item={item}
                    loanAmount={loanAmount}
                    loanTerm={loanTerm}
                    propositionId={propositionId}
                    isScored={false}
                    onTooltipPress={onShowRepresentativeExampleModal}
                />
            </Box>
        </UnscoredTableItemCard>
    )
}

export default UnscoredTableItem
